import { __awaiter } from "tslib";
import { getProspectingPlatform } from '@hooks/useProspectData/utils';
import { logger } from '@lusha/client-logger';
import { SUB_PLATFORM } from '@lusha/source-platforms';
import AppDispatcher from '@reducers/app/dispatcher';
import ContactsDispatcher from '@reducers/contacts/dispatcher';
import { getContactMapKey, mapSearchEmployeesResultToContacts } from '@reducers/contacts/mappers';
import { actions as searchCompanyActions } from '@reducers/searchCompany/reducer';
import { selectSearchCompany } from '@reducers/searchCompany/selectors';
import _ from 'lodash';
import { v4 as uuid } from 'uuid';
import { searchCompaniesByName, searchEmployeesByCompany } from '../../API/prospectingAPI';
import { SAVE_CONTACT_SUB_PLATFORMS } from '../../const/app';
import { mapSearchCompanyToCompany, resolveCompany } from './mappers';
import { actions } from './reducer';
class Dispatcher {
    constructor() {
        this.actions = actions;
    }
    setCompany(company) {
        return dispatch => {
            dispatch(actions.setCompany(company));
        };
    }
    setCompanies(companies) {
        return dispatch => {
            if (!companies.length) {
                return;
            }
            dispatch(actions.setCompanies(companies));
        };
    }
    updateCompany(key, changes) {
        return dispatch => {
            if (!key) {
                return;
            }
            dispatch(actions.updateCompany({ key, changes }));
        };
    }
    removeCompany(key) {
        return dispatch => {
            if (!key) {
                return;
            }
            dispatch(actions.removeCompany(key));
        };
    }
    fetchCompanyLid(company) {
        return (dispatch, getState) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const state = getState();
            const existing = resolveCompany(state.companies, company);
            const name = (existing === null || existing === void 0 ? void 0 : existing.name) || (company && typeof company === 'object' ? company.name : '') || '';
            if (!name) {
                return null;
            }
            if ((existing === null || existing === void 0 ? void 0 : existing.companyLid) != null) {
                return existing.companyLid;
            }
            try {
                const response = yield searchCompaniesByName({
                    company: name,
                    platform: 'Scout',
                    url: state.app.url || '',
                });
                const companyLid = (_c = (_b = (_a = response.companies) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.company_lid) !== null && _c !== void 0 ? _c : null;
                if (companyLid == null) {
                    return null;
                }
                if (existing) {
                    dispatch(actions.updateCompany({ key: existing.id, changes: { companyLid } }));
                    if (existing.id !== String(companyLid)) {
                        dispatch(actions.setCompany(Object.assign(Object.assign({}, existing), { id: String(companyLid), companyLid })));
                    }
                }
                return companyLid;
            }
            catch (error) {
                logger.info('Failed to fetch company lid: ', error);
                return null;
            }
        });
    }
    getCompany({ id, searchTrigger }) {
        return (dispatch, getState) => __awaiter(this, void 0, void 0, function* () {
            const state = getState();
            const company = state.companies[id];
            const searchQuery = yield dispatch(CompaniesDispatcher.getSearchQuery({ id, searchTrigger }));
            return dispatch(CompaniesDispatcher.searchCompany(searchQuery));
        });
    }
    searchCompany(searchQuery) {
        return (dispatch, getState) => __awaiter(this, void 0, void 0, function* () {
            const state = getState();
            const result = yield searchEmployeesByCompany(Object.assign(Object.assign({}, searchQuery), { externalPluginVersion: state.settings.version }));
            dispatch(this.setSearchEmployeesResult(result));
            dispatch(AppDispatcher.actions.setIsSearchLimitReached(Boolean(result.searchQuotaExceeded)));
            return result;
        });
    }
    getSearchQuery({ searchTrigger }) {
        return (dispatch, getState) => {
            const state = getState();
            const platform = state.settings.platform;
            const appContext = state.app.appContext;
            const searchCompany = selectSearchCompany(state, state.app.appContext);
            const prospectPlatform = getProspectingPlatform(platform);
            function getCompanySearchId() {
                const companySearchId = uuid();
                if (!searchCompany.companySearchId) {
                    dispatch(searchCompanyActions.setCompanySearchId({ context: appContext, companySearchId }));
                }
                return companySearchId;
            }
            /** The Search tab looks up the company picked in CompaniesSearch; the prospect tab uses the parsed page. */
            function getTargetCompany() {
                return {
                    name: searchCompany.name || undefined,
                    domain: searchCompany.domain || undefined,
                    company_lid: searchCompany.companyLid,
                    url: 'plugin://search',
                };
            }
            const { name, domain, company_lid, url } = getTargetCompany();
            const searchMetadata = {
                platform: SAVE_CONTACT_SUB_PLATFORMS[platform] || SUB_PLATFORM.LINKEDIN_MAIN,
            };
            return Object.assign(Object.assign({ domain: domain, name: name, platform: prospectPlatform, url: url, contextUrl: state.app.url || '', companySearchId: searchCompany.companySearchId ? searchCompany.companySearchId : getCompanySearchId(), filters: {}, searchTrigger, isRecentSearch: false }, (company_lid ? { company_lid: Number(company_lid) } : {})), { metadata: searchMetadata });
        };
    }
    setSearchEmployeesResult(result) {
        return dispatch => {
            const { contacts, company } = result || {};
            if (_.isEmpty(company))
                return null;
            const mappedCompany = mapSearchCompanyToCompany(company);
            if (mappedCompany) {
                dispatch(CompaniesDispatcher.setCompanies([mappedCompany]));
            }
            if (contacts === null || contacts === void 0 ? void 0 : contacts.length) {
                const mappedContacts = mapSearchEmployeesResultToContacts(result);
                dispatch(ContactsDispatcher.setContacts(mappedContacts));
                dispatch(actions.setCompanyEmployees({ key: company.company_lid, list: mappedContacts.map(getContactMapKey) }));
            }
        };
    }
}
const CompaniesDispatcher = new Dispatcher();
export default CompaniesDispatcher;
